"use client";

import { useCallback, useEffect } from "react";

import { ACCOUNT_COLORS } from "@/components/account/account-palette";
import {
  occupiedColumnWidth,
  useShellOccupancy,
} from "@/components/learn-piece/shell/shell-occupancy";
import { usePlaybackState } from "@/contexts/studio-context";
import { useAccountPrompt } from "@/hooks/journey/use-account-prompt";
import type { JourneySnapshot } from "@/lib/learning-journey/journey";

/**
 * « Enregistre ta progression. »
 *
 * # Pourquoi cette phrase et pas une demande
 *
 * Les trois autres points d'entrée du compte se déclenchent sur un blocage :
 * « Ajouter à mes morceaux », l'onglet « Mes morceaux », le header. On ne
 * demandait donc jamais de compte au moment où quelqu'un venait de *réussir*
 * quelque chose, seulement au moment où on lui refusait quelque chose.
 *
 * La phrase annonce le gain, le bouton porte le geste. Les faire dire la même
 * chose tous les deux, dans une capsule d'une ligne, serait du bruit.
 *
 * # Ce que « enregistre » simplifie, et pourquoi c'est assumé
 *
 * `syncing-progress-store` écrit en local d'abord, et le serveur « ne sert pas
 * à jouer, il sert à changer d'appareil » : sans compte, rien n'est perdu tant
 * qu'on reste sur ce navigateur. Une première version le disait exactement
 * (« ta progression ne vit que dans ce navigateur »), mais personne ne lit une
 * nuance de stockage sur un roll en pause. « Enregistre » est plus grossier
 * sans être faux : ce qui n'est pas sur le compte s'en va avec les données du
 * site, avec le navigateur, avec l'appareil. Arbitrage produit, pas étourderie.
 *
 * # Pourquoi elle n'ouvre pas un formulaire elle-même
 *
 * `AccountScreen` existe, il est monté globalement et il sait tout faire, y
 * compris revendiquer les morceaux déposés en anonyme. La capsule n'est qu'une
 * porte : un clic, et c'est l'écran de compte qui prend la suite.
 *
 * # Où elle se pose
 *
 * Au même endroit que la capsule de retour, dont elle est exclusive : en bas
 * du roll, **sous la playhead**, parce que ce qui est en dessous est déjà
 * joué. Le calcul de centrage sur la bande libre est repris du voisin plutôt
 * que partagé — huit lignes de `calc` ne valent pas de toucher à un composant
 * réglé au pixel. `shell-occupancy` explique pourquoi une constante ne peut
 * pas remplacer cette lecture.
 */

/**
 * Au-delà, le titre cesse d'aider : les MIDI importés portent des noms de
 * fichier entiers, et une capsule qui s'allonge pour nommer un morceau ne
 * parle plus de la progression. On retombe alors sur la phrase sans titre.
 *
 * Vingt-huit tenait tout juste « Prélude en Do majeur BWV 846 » du temps où la
 * phrase expliquait le stockage. Elle a raccourci, donc le plafond n'est plus
 * la contrainte de mise en page qu'il était : il ne sert plus qu'à écarter les
 * noms de fichier. Le laisser tel quel évite de rouvrir un réglage au pixel
 * pour un gain que personne ne verrait.
 */
const MAX_TITLE_CHARS = 28;

export type AccountPromptProps = {
  readonly snapshot: JourneySnapshot | null;
  /** Titre du morceau en cours, chaîne vide quand il n'y en a pas. */
  readonly pieceTitle: string;
};

export function AccountPrompt({ snapshot, pieceTitle }: AccountPromptProps) {
  const { visible, accept, dismiss } = useAccountPrompt({ snapshot });
  const playing = usePlaybackState();

  const title = pieceTitle.trim();
  const namesThePiece = title !== "" && title.length <= MAX_TITLE_CHARS;

  // La lecture repart : la personne a répondu avec ses mains. Une capsule
  // posée sur le roll pendant une passe est exactement l'interruption qu'on
  // refuse.
  useEffect(() => {
    if (playing && visible) {
      dismiss();
    }
  }, [playing, visible, dismiss]);

  // Seule la colonne (bord droit) prend une largeur ; une bande ou une
  // capsule centrée n'en occupent aucune, et s'en écarter latéralement
  // décentrerait l'invitation pour rien.
  const blocked = occupiedColumnWidth(useShellOccupancy());
  const freeBand = `(100% - ${blocked}px)`;
  const centerOfFreeBand = `calc(${freeBand} / 2)`;

  // Écouteur **local**, jamais sur `window` : un gestionnaire global volerait
  // Échap aux modales et au shell.
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        dismiss();
      }
    },
    [dismiss],
  );

  if (!visible) {
    return null;
  }

  return (
    <section
      role="region"
      aria-label="Invitation à créer un compte"
      onKeyDown={handleKeyDown}
      /* Même geste d'entrée que la capsule de retour, au même endroit et au
         même instant : deux animations pour un seul emplacement seraient deux
         façons de dire la même chose. */
      className="pointer-events-auto absolute bottom-3 -translate-x-1/2 z-40 feedback-prompt-enter"
      style={{
        left: centerOfFreeBand,
        /*
          480 px, comme le voisin : la phrase tient sur une ligne à cette
          largeur. Elle a occupé 620 px tant qu'elle expliquait le stockage.
        */
        width: `min(480px, calc(${freeBand} - 32px))`,
        backgroundColor: "var(--color-glass-shell)",
        backdropFilter: "blur(var(--blur-shell)) saturate(140%)",
        WebkitBackdropFilter: "blur(var(--blur-shell)) saturate(140%)",
        border: "1px solid var(--color-glass-border)",
        borderRadius: "var(--radius-tile)",
        boxShadow: "var(--shadow-popin)",
      }}
    >
      <div className="flex items-center gap-3 py-2.5 pl-4 pr-2.5">
        {/* L'annonce est `polite` : elle doit attendre que le lecteur d'écran
            ait fini ce qu'il disait, pas couper la parole. */}
        <p
          aria-live="polite"
          aria-atomic="true"
          className="flex-1 text-[13px] leading-[18px]"
          style={{ color: ACCOUNT_COLORS.text }}
        >
          {namesThePiece ? (
            <>Enregistre ta progression sur «&nbsp;{title}&nbsp;».</>
          ) : (
            <>Enregistre ta progression.</>
          )}
        </p>

        <button
          type="button"
          onClick={accept}
          className="h-[30px] shrink-0 cursor-pointer rounded-lg px-3 text-[13px] font-semibold text-white transition-opacity duration-150 hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          style={{ backgroundColor: ACCOUNT_COLORS.accent }}
        >
          Créer un compte
        </button>

        {/* La porte de sortie, discrète et sans conséquence : l'invitation
            pourra revenir une fois la progression devenue plus lourde. Elle ne
            dit donc pas « ne plus proposer », contrairement à celle du
            retour. */}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Fermer"
          className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-[15px] leading-none transition-colors duration-150 cursor-pointer hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          style={{ color: ACCOUNT_COLORS.textMuted }}
        >
          ×
        </button>
      </div>
    </section>
  );
}
